/**
 * @description
 * React Query hook for patient medication data.
 * Fetches live data from the FastAPI backend via the centralized backend-api service.
 * Automatically falls back to mock data when the backend is unreachable so that
 * local development continues to work without a running backend.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { Medication } from '../types/medication-types';
import { getMedications } from '../services/backend-api';

// Query key factory — keep stable so other callers can invalidate the cache
export const medicationKeys = {
  all: ['medications'] as const,
  list: () => [...medicationKeys.all, 'list'] as const,
  listByPatient: (patientId: string) => [...medicationKeys.list(), patientId] as const,
};

/**
 * Custom hook to fetch medications for the currently authenticated patient.
 *
 * @returns React Query result extended with convenience fields:
 *   - `medications`          — all fetched medications (defaults to [])
 *   - `activeMedicationsCount` — count of medications with status === 'active'
 */
export const useMedications = () => {
  const { user } = useAuth();
  const userId = user?.id;

  const queryResult = useQuery<Medication[], Error>({
    queryKey: userId ? medicationKeys.listByPatient(userId) : medicationKeys.list(),
    queryFn: async () => {
      if (!userId) return [];
      return getMedications(userId);
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes — medications change infrequently
    retry: (failureCount, error) => {
      // Don't retry if the error is a 4xx (auth / not found); do retry transient failures.
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });

  return {
    ...queryResult,
    medications: queryResult.data ?? [],
    activeMedicationsCount:
      queryResult.data?.filter((m) => m.status === 'active').length ?? 0,
  };
};
